import { uniq } from 'es-toolkit';
import React, { useMemo } from 'react';
import { Plugin, Template, TemplatePlaceholder } from '@devexpress/dx-react-core';
import { Box } from '@mui/material';

import { ProductionComponentInstanceStatus } from '@paragon/contract-library';
import { getRanges, humanCase } from '@paragon/utilities';

import { loadAllDataForProject } from '../../../actions/loadAllDataForProject';
import { getStatusesByBundleGuid } from '../../../selectors/bundles';
import { getProjectLayoutModels } from '../../../selectors/getProjectLayoutModels';
import {
  getTruckloadGridCheckedBundleGuids,
  getTruckloadGridRows,
  isGroupedProductionComponentInstanceRow,
} from '../../../selectors/truckloadGrid';
import { collapseRows, expandRows, setProjectGuidFilter } from '../../../store/truckloadGridSlice';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { CollapseAllButton } from '../CollapseAllButton';
import { ExpandAllButton } from '../ExpandAllButton';
import { ProjectSelection } from '../ProjectSelection';
import { SetStatus } from '../SetStatus';

const allowedBundleStatusesForMarkingShippedOrDelivered: readonly ProductionComponentInstanceStatus[] = [
  'FinishedAssembly',
  'StoredOnYard',
  'Shipped',
  'Delivered',
];

export function TruckloadGridToolbarPlugin() {
  const dispatch = useAppDispatch();
  const checkedBundleGuids = useAppSelector(getTruckloadGridCheckedBundleGuids);
  const bundles = useAppSelector((state) => state.bundles);
  const truckloads = useAppSelector((state) => state.truckloads);
  const statusesByBundleGuid = useAppSelector(getStatusesByBundleGuid);
  const projectGuidFilter = useAppSelector((state) => state.truckloadGrid.projectGuidFilter);
  const projects = useAppSelector(getProjectLayoutModels);

  const checkedBundles = useMemo(
    () => checkedBundleGuids.map((bundleGuid) => bundles[bundleGuid]),
    [checkedBundleGuids, bundles],
  );

  const checkedBundlesWithDisallowedStatusForMarkingShippedOrDelivered = checkedBundles.filter(
    (bundle) =>
      statusesByBundleGuid[bundle.guid].filter(
        (status) => !allowedBundleStatusesForMarkingShippedOrDelivered.includes(status),
      ).length > 0,
  );

  const truckloadsForCheckedBundles = useMemo(
    () => uniq(checkedBundles.map((bundle) => truckloads[bundle.truckloadGuid!])),
    [checkedBundles, truckloads],
  );
  const truckloadsForCheckedBundlesDoNotHaveAllRequiredProperties = useMemo(
    () =>
      truckloadsForCheckedBundles.some(
        (truckload) =>
          truckload.trailerGuid === null || truckload.truckDriverGuid === null || truckload.dateShipped === null,
      ),
    [truckloadsForCheckedBundles],
  );

  const setStatusDisabled =
    checkedBundlesWithDisallowedStatusForMarkingShippedOrDelivered.length > 0 ||
    truckloadsForCheckedBundlesDoNotHaveAllRequiredProperties;
  const setStatusDisabledReason =
    checkedBundlesWithDisallowedStatusForMarkingShippedOrDelivered.length > 0
      ? `All checked bundles must have only these statuses to be marked "Shipped" or "Delivered": ${allowedBundleStatusesForMarkingShippedOrDelivered
          .map(humanCase)
          .join(', ')}. Check these bundles and make updates in other tabs: ${getRanges(
          checkedBundlesWithDisallowedStatusForMarkingShippedOrDelivered.map((bundle) => bundle.id),
        ).join(', ')}.`
      : truckloadsForCheckedBundlesDoNotHaveAllRequiredProperties
        ? 'Specify the trailer, truck driver, and ship date for the truckload(s) before updating the status'
        : undefined;

  const truckloadGridRows = useAppSelector(getTruckloadGridRows);
  const allExpandableOrCollapsableRowIds = truckloadGridRows
    .filter((row) => !isGroupedProductionComponentInstanceRow(row.rowId))
    .map((row) => row.rowId);

  function onSelectProjectFilter(projectGuid: string | null) {
    dispatch(setProjectGuidFilter(projectGuid));

    if (projectGuid === null) return;
    // When filtering to a project, load the components for each of the production groups associated with that project
    // so that filtering for the component names will work
    dispatch(loadAllDataForProject(projectGuid));
  }

  return (
    <Plugin name="TruckloadGridToolbar" dependencies={[{ name: 'Toolbar' }]}>
      <Template name="toolbarContent">
        <Box sx={{ display: 'flex', flexDirection: 'row', verticalAlign: 'middle' }}>
          <Box sx={{ width: 600, p: 1 }}>
            <ProjectSelection
              activeProjectGuid={projectGuidFilter}
              projects={projects}
              setActiveProjectGuid={onSelectProjectFilter}
            />
          </Box>
          <Box sx={{ width: 380, p: 1 }}>
            <SetStatus
              checkedBundleGuids={checkedBundleGuids}
              options={['Shipped', 'Delivered']}
              disabled={setStatusDisabled}
              disabledReason={setStatusDisabledReason}
            />
          </Box>
        </Box>
        <TemplatePlaceholder />
        <ExpandAllButton
          expandAll={(rowIds) => dispatch(expandRows(rowIds))}
          allExpandableRowIds={allExpandableOrCollapsableRowIds}
        />
        <CollapseAllButton
          collapseAll={(rowIds) => dispatch(collapseRows(rowIds))}
          allCollapsableRowIds={allExpandableOrCollapsableRowIds}
        />
      </Template>
    </Plugin>
  );
}
